import PrintIcon from "@mui/icons-material/Print";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import React, { useState } from "react";

import { BackdropCustom } from "../backdrop/Backdrop";
import { useToastr } from "../toast/Toast";
import api from "../../services/api";
import { openBase64Pdf } from "../../utils/downloadPdf";
import { getUtcOffset } from "../../utils/timezone";

interface IPrintPdfDialogProps {
  /** Se true, exibe o botão que abre o diálogo */
  showButton?: boolean;
  /** Label do botão gatilho */
  buttonLabel?: string;
  /** O diálogo é controlado externamente (sem botão interno) */
  open?: boolean;
  /** Callback externo para fechar o diálogo */
  onClose?: () => void;
  /** ID da exposição para o endpoint */
  exhibitionId: string;
  /** Quando true, habilita as opções de split (separado por colecionador) */
  allowSplit?: boolean;
  /**
   * Modo de geração:
   * - "download": faz download do arquivo (padrão — Exhibitions.tsx)
   * - "open": abre o PDF numa nova aba (Reception.tsx)
   */
  mode?: "download" | "open";
}

/**
 * Dialog reutilizável para imprimir/baixar o relatório de plantas de uma
 * exposição via endpoint /collector/plants/report/exhibition/{exhibitionId}.
 *
 * Pode ser usado de duas formas:
 * 1. Com botão interno: <PrintPdfDialog showButton exhibitionId={id} />
 * 2. Controlado externamente: <PrintPdfDialog open={open} onClose={fn} exhibitionId={id} />
 */
export const PrintPdfDialog: React.FC<IPrintPdfDialogProps> = ({
  showButton = false,
  buttonLabel = "Imprimir Relatório",
  open: externalOpen,
  onClose: externalOnClose,
  exhibitionId,
  allowSplit = true,
  mode = "download",
}) => {
  const toastr = useToastr();
  const [internalOpen, setInternalOpen] = useState(false);
  const [onlyReceived, setOnlyReceived] = useState(true);
  const [loading, setLoading] = useState(false);

  // Suporte a uso controlado externamente ou via botão interno
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;

  const handleOpen = () => {
    setOnlyReceived(true);
    setInternalOpen(true);
  };

  const handleClose = () => {
    if (externalOnClose) externalOnClose();
    setInternalOpen(false);
  };

  const base64ToPdf = (
    base64: string,
    filename: string,
    openMode: "download" | "open",
    windowRef?: Window | null,
  ) => {
    if (openMode === "open") {
      openBase64Pdf(base64, filename, windowRef);
    } else {
      const base64Clean = base64.replace(/^data:application\/pdf;base64,/, "");
      const byteCharacters = atob(base64Clean);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleGenerate = async (split: boolean) => {
    if (!exhibitionId) {
      toastr.error("Exposição não encontrada");
      return;
    }

    let newWindow: Window | null = null;
    if (mode === "open") {
      newWindow = window.open("", "_blank");
      if (newWindow) {
        newWindow.document.write("<p>Gerando relatório... Aguarde.</p>");
      }
    }

    handleClose();
    setLoading(true);
    try {
      const response = await api.get(
        `/collector/plants/report/exhibition/${exhibitionId}`,
        {
          params: {
            ...(allowSplit ? { split } : {}),
            ...(onlyReceived ? { onlyReceived: true } : {}),
            utcOffset: getUtcOffset(),
          },
        },
      );

      const files = response.data;
      const filesArray = Array.isArray(files) ? files : [files];

      if (
        filesArray.length === 0 ||
        (!filesArray[0]?.base64 && !filesArray[0]?.filename)
      ) {
        toastr.error("Nenhum relatório encontrado para gerar");
        if (newWindow) newWindow.close();
        return;
      }

      filesArray.forEach((file: { filename: string; base64: string }, index: number) => {
        if (!file.base64 || !file.filename) return;
        const win = index === 0 ? newWindow : null;
        base64ToPdf(file.base64, file.filename, mode, win);
      });
    } catch (error: any) {
      if (newWindow) newWindow.close();
      toastr.error(
        error?.response?.data?.message || "Erro ao gerar relatório de plantas",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showButton && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<PrintIcon />}
          onClick={handleOpen}
          disabled={!exhibitionId}
          size="small"
        >
          {buttonLabel}
        </Button>
      )}

      <Dialog
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="print-pdf-dialog-title"
        aria-describedby="print-pdf-dialog-description"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <DialogTitle id="print-pdf-dialog-title">
          Como deseja gerar o relatório de plantas?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="print-pdf-dialog-description" sx={{ mb: 2 }}>
            {allowSplit
              ? "Selecione se deseja gerar os relatórios separados por colecionador ou um único relatório compilado."
              : "Confirme para gerar o relatório de plantas desta exposição."}
          </DialogContentText>
          <Tooltip
            title="Gera o relatório em PDF contendo apenas as plantas marcadas como recebidas."
            arrow
            placement="right"
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyReceived}
                  onChange={e => setOnlyReceived(e.target.checked)}
                  color="primary"
                />
              }
              label="Apenas plantas recebidas"
            />
          </Tooltip>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancelar
          </Button>
          {allowSplit ? (
            <>
              <Button
                onClick={() => handleGenerate(false)}
                color="primary"
              >
                Relatório Único
              </Button>
              <Button
                onClick={() => handleGenerate(true)}
                color="primary"
                variant="contained"
                autoFocus
              >
                Separado por Colecionador
              </Button>
            </>
          ) : (
            <Button
              onClick={() => handleGenerate(false)}
              color="primary"
              variant="contained"
              autoFocus
            >
              Gerar Relatório
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {loading && <BackdropCustom />}
    </>
  );
};
